/**
 * La versión nueva: whether this phone runs what the server serves now, and getting it there.
 *
 * Until 2026-10-09 a new version was only offered: a bar at the bottom of the screen
 * (`ui/ActualizacionDisponible.tsx`) that «Después» put away for as long as anybody liked. A
 * phone on last week's version does not know last week's rules. It sells the goods an order has
 * set aside, or past what is in stock without the owner's yes, because to its code those settings
 * do not exist. So the owner asked for the new version to be required (2026-10-09: «if there is an
 * update the user should have to click update to continue»): after the PIN, and on opening with
 * no PIN to ask for, the app checks before anything of it shows, and with a new version the only
 * way on is «Actualizar» (`ui/VersionNueva.tsx`).
 *
 * The check is the service worker's own. `registration.update()` fetches `sw.js`, two kilobytes
 * that list every file of the build by its hash, and compares it with the one running: any change
 * to the app shows, and a change to the server alone, which needs nothing from the phone, does
 * not. A page no service worker controls came from the server just now (a first visit, or
 * `pnpm dev`, which registers none), so it is what the server serves.
 *
 * With no connection there is nothing to compare with and nothing to download, and the till keeps
 * selling with no signal («La caja sigue vendiendo», `Pos.tsx`). So a check that has not heard
 * from the server in a few seconds lets the person in. A version already downloaded is the
 * exception: it installs with no connection, so it is required all the same.
 */

/** How long a check waits on the server before letting the person in. */
const ESPERA_MS = 4_000;

/** How long the new version may take to download once «Actualizar» is pressed. */
const DESCARGA_MS = 120_000;

/** And to take over from the old one once it has: a moment, unless something is wrong. */
const RELEVO_MS = 10_000;

/**
 * A check this recent answers for another: the one the PIN starts, for the door it opens a moment
 * later. No longer, or a version put up in the meantime would be missed.
 */
const VIGENCIA_MS = 5_000;

/** Between checks while the app stays open, for the bar at the bottom of the screen. */
const CADA_MS = 15 * 60_000;

/** Up to date; a newer version is waiting; or the server could not be asked. */
export type Revision = 'al-dia' | 'nueva' | 'sin-revisar';

/**
 * Whether there can be anything to check: a service worker controls the page. Synchronous, so
 * that where there is none (`pnpm dev`, the browser tests that run on it, a first visit) nothing
 * stands in the way, not even for a frame.
 */
export const puedeHaberVersionNueva = (): boolean => Boolean(navigator.serviceWorker?.controller);

let ultima: { desde: number; respuesta: Promise<Revision> } | undefined;

/**
 * Whether a newer version is waiting. One check at a time, and one from the last few seconds
 * answers for a new one, so the PIN can start it and the door it opens wait on the same. One that
 * could not reach the server answers nothing after it: the next asks again.
 */
export function revisarVersion(): Promise<Revision> {
  const ahora = Date.now();
  if (ultima && ahora - ultima.desde < VIGENCIA_MS) return ultima.respuesta;
  const esta = { desde: ahora, respuesta: preguntar().catch((): Revision => 'sin-revisar') };
  ultima = esta;
  void esta.respuesta.then((r) => {
    if (r === 'sin-revisar' && ultima === esta) ultima = undefined;
  });
  return esta.respuesta;
}

async function preguntar(): Promise<Revision> {
  const trabajadores = navigator.serviceWorker;
  if (!trabajadores?.controller) return 'al-dia';
  const registro = await trabajadores.getRegistration();
  if (!registro) return 'al-dia';
  // Downloaded already, by an earlier check, or put off with the bar's «Después»: installing it
  // needs no connection.
  if (registro.waiting) return 'nueva';
  if (!navigator.onLine) return 'sin-revisar';
  await aTiempo(registro.update(), ESPERA_MS);
  // `update()` settles once a different `sw.js` has started installing.
  return registro.installing || registro.waiting ? 'nueva' : 'al-dia';
}

/**
 * Puts the new version in and opens it: waits for it to finish downloading, has it take over,
 * reloads. What was saved stays, sent or not (it is in IndexedDB), and so does the sale on the
 * till (`sessionStorage` outlives a reload). If the new version never arrives this fails instead
 * of reloading into the old one again, and the screen that asked says so.
 */
export async function actualizarAhora(): Promise<void> {
  const registro = await navigator.serviceWorker?.getRegistration();
  let nueva = registro?.waiting ?? registro?.installing;
  if (registro && !nueva) {
    // Found, and failed to download since: from the start.
    await aTiempo(registro.update(), ESPERA_MS);
    nueva = registro.waiting ?? registro.installing;
  }
  // Nothing newer after all (the server went back to this version): the page as it is.
  if (!nueva) return location.reload();
  await instalada(nueva);
  await alMando(nueva);
  location.reload();
}

/** Once the new version has finished downloading. Rejects if it fails to, or takes too long. */
function instalada(nueva: ServiceWorker): Promise<void> {
  return aTiempo(
    new Promise<void>((listo, fallo) => {
      const mirar = () => {
        if (nueva.state === 'installing' || nueva.state === 'parsed') return;
        nueva.removeEventListener('statechange', mirar);
        if (nueva.state === 'redundant') fallo(new Error('la versión nueva no se instaló'));
        else listo();
      };
      nueva.addEventListener('statechange', mirar);
      mirar();
    }),
    DESCARGA_MS,
  );
}

/** Has it take over from the version running now. */
function alMando(nueva: ServiceWorker): Promise<void> {
  const trabajadores = navigator.serviceWorker;
  if (trabajadores.controller === nueva) return Promise.resolve();
  return aTiempo(
    new Promise<void>((listo) => {
      trabajadores.addEventListener('controllerchange', () => listo(), { once: true });
      // vite-plugin-pwa's prompt mode builds `sw.js` to answer this with `skipWaiting()`.
      nueva.postMessage({ type: 'SKIP_WAITING' });
    }),
    RELEVO_MS,
  );
}

/**
 * Checks now and then while the app stays open, and when the connection comes back, so that a
 * phone nobody locks still hears of a new version: the bar at the bottom of the screen says so
 * once it has downloaded, and the next PIN requires it.
 */
export function vigilarVersiones(): () => void {
  if (!navigator.serviceWorker) return () => {};
  const revisar = () => {
    if (document.visibilityState === 'visible') void revisarVersion();
  };
  const reloj = setInterval(revisar, CADA_MS);
  window.addEventListener('online', revisar);
  return () => {
    clearInterval(reloj);
    window.removeEventListener('online', revisar);
  };
}

function aTiempo<T>(promesa: Promise<T>, ms: number): Promise<T> {
  let reloj: ReturnType<typeof setTimeout> | undefined;
  const plazo = new Promise<never>((_, fallo) => {
    reloj = setTimeout(() => fallo(new Error('sin respuesta')), ms);
  });
  return Promise.race([promesa, plazo]).finally(() => clearTimeout(reloj));
}

/** For the tests: forget the last check. */
export function olvidarRevision(): void {
  ultima = undefined;
}
